import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import {
  Tabs,
  Table,
  Badge,
  Avatar,
  Modal,
  Button,
  Input,
  Text,
  Card,
} from '@teals/ui-web';
import type { TabItem, Column } from '@teals/ui-web';
import { useAuth } from '../../hooks/useAuth';
import {
  useGetOrganisationQuery,
  useUpdateOrganisationMutation,
  useGetMembersQuery,
  useInviteMemberMutation,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  useGetPendingInvitationsQuery,
  useCancelInvitationMutation,
  useResendInvitationMutation,
  useGetRolesQuery,
  useCreateRoleMutation,
  useUpdateRoleMutation,
  useDeleteRoleMutation,
} from '../../store/api/portalApi';
import type { Member, Invitation, Role } from '../../store/api/portalApi';
import {
  useGetApiKeyProfilesQuery,
  useCreateApiKeyProfileMutation,
  useRegenerateApiKeyProfileMutation,
  useDeleteApiKeyProfileMutation,
} from '../../store/api/apiKeyProfilesApi';
import type { ApiKeyProfile } from '../../store/api/apiKeyProfilesApi';
import { useGetApplicationsQuery } from '../../store/api/applicationsApi';
import { buildInviteLink } from '../../utils/inviteLink';

function getMutationErrorMessage(error: unknown): string {
  const fallback = 'Something went wrong. Please try again.';
  try {
    const data = (error as { data?: unknown })?.data;
    if (!data || typeof data !== 'object') return fallback;
    const record = data as Record<string, unknown>;
    if (typeof record.message === 'string') return record.message;
    return fallback;
  } catch {
    return fallback;
  }
}

type BusinessTab = 'members' | 'api-keys' | 'roles' | 'settings';

const ROLE_VARIANT_MAP: Record<
  string,
  'primary' | 'info' | 'success' | 'neutral'
> = {
  owner: 'primary',
  admin: 'info',
  builder: 'success',
  member: 'success',
  viewer: 'neutral',
};

const SYSTEM_ROLE_DESCRIPTIONS: Record<string, string> = {
  owner: 'Full access to all organisation resources',
  admin: 'Manage team and resources',
  builder: 'Create and configure agents and data sources',
  member: 'Create and configure agents and data sources',
  viewer: 'Read-only access to agents and analytics',
};

const SYSTEM_ROLE_PERMISSION_CHIPS: Record<string, string[]> = {
  owner: [
    'Members',
    'Agents',
    'Applications',
    'Data Sources',
    'Analytics',
    'Billing',
    'Settings',
  ],
  admin: ['Members', 'Agents', 'Applications', 'Data Sources', 'Analytics'],
  builder: ['Agents', 'Applications', 'Data Sources'],
  member: ['Agents', 'Applications', 'Data Sources'],
  viewer: ['View Agents', 'View Analytics'],
};

const PERMISSION_SETS = [
  { id: 'user_management_permission_set', label: 'User management' },
  { id: 'member_management_permission_set', label: 'Member management' },
  { id: 'invite_user_permission_set', label: 'Invitations' },
  { id: 'role_management_permission_set', label: 'Roles & permissions' },
  { id: 'organisation_management_permission_set', label: 'Organisation settings' },
  { id: 'view_only_permission_set', label: 'View only' },
] as const;

const PERMISSION_SET_LABELS: Record<string, string> = Object.fromEntries(
  PERMISSION_SETS.map((p) => [p.id, p.label])
);

/* ---------- Layout ---------- */

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem 1.5rem 3rem;

  @media (max-width: 768px) {
    padding: 1.5rem 1rem 2rem;
  }
`;

const TabsBar = styled.div`
  width: 100%;
  border-bottom: 1px solid var(--border);
`;

const OrgBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 1.25rem;
  padding: 1.5rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const OrgAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  font-weight: 700;
  flex-shrink: 0;
`;

const OrgInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const OrgTitle = styled.h2`
  margin: 0 0 0.125rem;
  font-size: 1.75rem;
  font-weight: 600;
  color: var(--text-primary);
  letter-spacing: -0.02em;
`;

const OrgSubtitle = styled.p`
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin: 0 0 0.5rem;
`;

const OrgStatsInline = styled.p`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0;

  span {
    margin-right: 0.25rem;
  }
`;

const KpiStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 480px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const KpiCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
  min-width: 0;
`;

const KpiLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const KpiValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
`;

const TabPanel = styled.div`
  padding-top: 1.5rem;
`;

const TabHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const TabHeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const MemberCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RoleSelect = styled.select`
  padding: 0.375rem 0.625rem;
  border-radius: 6px;
  border: 1px solid var(--border);
  background: var(--input-bg, var(--bg-surface));
  font-family: inherit;
  font-size: 0.8125rem;
  color: var(--text-secondary);
  cursor: pointer;
  outline: none;

  &:focus {
    border-color: var(--accent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PendingSection = styled.div`
  margin-top: 2rem;
`;

const PendingCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 10px;
  margin-bottom: 0.75rem;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: var(--shadow-sm, 0 1px 2px rgba(0, 0, 0, 0.04));
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const PendingInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const PendingEmail = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
`;

const PendingMeta = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const PendingActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ProfileCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: var(--shadow-md, 0 4px 12px rgba(0, 0, 0, 0.06));
  }
`;

const ProfileCardHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 1rem;
`;

const ProfileName = styled.div`
  font-size: 1.0625rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const ProfileId = styled.div`
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
  color: var(--text-muted);
`;

const ProfileApps = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-bottom: 1rem;
`;

const AppChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.625rem;
  background: var(--accent-subtle, #e8f7f6);
  color: var(--accent);
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const ProfileMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-bottom: 1rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const ProfileMetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const ProfileMetaLabel = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
`;

const KeyDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--code-bg, var(--bg-elevated));
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: 'SF Mono', 'Fira Code', monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  margin-top: 0.375rem;
`;

const KeyValue = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CopyBtn = styled.button`
  flex-shrink: 0;
  cursor: pointer;
  color: var(--text-muted);
  background: none;
  border: none;
  font-size: 0.875rem;
  font-family: inherit;

  &:hover {
    color: var(--accent);
  }
`;

const CreateProfileCard = styled.div`
  border: 2px dashed var(--border);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
`;

const InfoBanner = styled.div<{ $variant?: 'info' | 'warning' | 'success' }>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  font-size: 0.8125rem;
  margin-bottom: 1rem;
  background: ${(p) =>
    p.$variant === 'warning'
      ? 'var(--warning-subtle, #fff7ed)'
      : p.$variant === 'success'
        ? 'var(--success-subtle, #f0fdf4)'
        : 'var(--info-subtle, #eff6ff)'};
  color: ${(p) =>
    p.$variant === 'warning'
      ? 'var(--warning)'
      : p.$variant === 'success'
        ? 'var(--success)'
        : 'var(--info, #3b82f6)'};
`;

const FormFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 520px;
`;

const CheckboxGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  cursor: pointer;
`;

const SuccessCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--success);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 1rem;
`;

const SuccessField = styled.div`
  margin-bottom: 0.75rem;
`;

const SuccessFieldLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 0.375rem;
`;

const RolesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RoleCard = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: var(--shadow-md, 0 4px 12px rgba(0, 0, 0, 0.06));
  }
`;

const RoleCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.5rem;
`;

const RoleMemberCount = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const RoleDescription = styled.p`
  font-size: 0.8125rem;
  color: var(--text-secondary);
  margin: 0 0 0.75rem;
`;

const PermissionsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
`;

const PermissionChip = styled.span<{ $readonly?: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 0.1875rem 0.5rem;
  background: ${(p) => (p.$readonly ? 'transparent' : 'var(--bg-elevated)')};
  color: ${(p) => (p.$readonly ? 'var(--text-muted)' : 'var(--text-secondary)')};
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 500;
  border: ${(p) =>
    p.$readonly ? '1px dashed var(--border-strong, var(--border))' : 'none'};
`;

const RoleActions = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const SettingsForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  max-width: 520px;
`;

const UploadPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  border: 2px dashed var(--border);
  border-radius: 12px;
  color: var(--text-muted);
  font-size: 0.75rem;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;

  &:hover {
    border-color: var(--accent);
  }
`;

const DangerZone = styled.div`
  background: var(--bg-surface);
  border: 1px solid var(--error, #ef4444);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;

  h4 {
    color: var(--error, #ef4444);
    margin: 0 0 0.5rem;
  }

  p {
    font-size: 0.875rem;
    color: var(--text-secondary);
    margin: 0 0 1rem;
  }
`;

const ModalForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const SelectWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const SelectLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const Select = styled.select`
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  font-size: 1rem;
  color: var(--text-primary);
  background: var(--bg-surface);
  outline: none;

  &:focus {
    border-color: var(--accent);
  }
`;

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: var(--text-secondary);
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  background: var(--error-subtle, #fef2f2);
  border: 1px solid var(--border-danger, #fecaca);
  border-radius: 0.5rem;
  color: var(--error, #dc2626);
  font-size: 0.875rem;
  margin-bottom: 1rem;
`;

const SuccessBanner = styled.div`
  padding: 0.75rem 1rem;
  background: var(--success-subtle, #ecfdf5);
  border: 1px solid var(--border-success, #a7f3d0);
  border-radius: 0.5rem;
  color: var(--text-success, #047857);
  font-size: 0.875rem;
`;

const InviteLinkRow = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const InviteLinkInput = styled.input`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 0.8125rem;
  color: var(--text-primary);
  background: var(--bg-inset);
`;

const TextArea = styled.textarea`
  padding: 0.75rem 1rem;
  background: var(--input-bg, var(--bg-surface));
  border: 1px solid var(--border);
  border-radius: 8px;
  font-family: inherit;
  font-size: 0.9375rem;
  color: var(--text-primary);
  outline: none;
  width: 100%;
  resize: vertical;
  min-height: 80px;

  &:focus {
    border-color: var(--accent);
  }
`;

const StatusDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background: var(--success);
  margin-right: 0.25rem;
`;

function getDisplayName(member: Member): string {
  const first = member.firstName || '';
  const last = member.lastName || '';
  const full = `${first} ${last}`.trim();
  return full || member.email || 'Unknown';
}

function getOrgInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'OR';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}

function formatDate(iso: string | undefined | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function formatRelative(iso: string | undefined | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    return formatDate(iso);
  } catch {
    return iso;
  }
}

function parsePermissionsConfig(
  config: Role['permissionsConfig']
): Record<string, unknown> | null {
  if (!config) return null;
  if (typeof config === 'string') {
    try {
      return JSON.parse(config) as Record<string, unknown>;
    } catch {
      return null;
    }
  }
  return config;
}

function getPermissionSetIds(role: Role): string[] {
  const config = parsePermissionsConfig(role.permissionsConfig);
  if (!config) return [];
  const sets = config.permission_sets as { permission_sets_items?: string[] } | undefined;
  return sets?.permission_sets_items ?? [];
}

function buildPermissionsConfig(selectedSets: string[]): Record<string, unknown> {
  return {
    scope: 'org',
    permission_sets: {
      permission_sets_items: selectedSets,
      global_action: 'allow',
    },
    permissions: {},
  };
}

function pickDefaultInviteRoleId(roles: Role[]): string {
  const byName = (name: string) =>
    roles.find((r) => r.name.toLowerCase() === name)?.id;
  return (
    byName('member') || byName('builder') || byName('viewer') || roles[0]?.id || ''
  );
}

function invitationRoleName(inv: Invitation): string {
  return inv.roleName || inv.role?.name || 'member';
}

const BusinessCenter: React.FC = () => {
  const { user } = useAuth();
  const orgId = user?.organizationId;

  const [activeTab, setActiveTab] = useState<BusinessTab>('members');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRoleId, setInviteRoleId] = useState('');
  const [sendInviteEmail, setSendInviteEmail] = useState(true);
  const [inviteSuccessLink, setInviteSuccessLink] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [orgName, setOrgName] = useState('');
  const [orgDescription, setOrgDescription] = useState('');
  const [page] = useState(1);

  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleName, setRoleName] = useState('');
  const [selectedPermissionSets, setSelectedPermissionSets] = useState<string[]>([
    'view_only_permission_set',
  ]);
  const [roleError, setRoleError] = useState<string | null>(null);
  const [rolesActionError, setRolesActionError] = useState<string | null>(null);

  const [showCreateProfile, setShowCreateProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profileDescription, setProfileDescription] = useState('');
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const [createdProfile, setCreatedProfile] = useState<ApiKeyProfile | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);

  const { data: org, isLoading: orgLoading } = useGetOrganisationQuery(orgId!, {
    skip: !orgId,
  });

  const {
    data: membersData,
    isLoading: membersLoading,
    error: membersError,
  } = useGetMembersQuery({ orgId: orgId!, page, pageSize: 20 }, { skip: !orgId });

  const { data: invitations, isLoading: invitationsLoading } =
    useGetPendingInvitationsQuery(orgId!, { skip: !orgId });

  const { data: roles, isLoading: rolesLoading } = useGetRolesQuery(orgId!, {
    skip: !orgId,
  });

  const { data: profiles, isLoading: profilesLoading } = useGetApiKeyProfilesQuery(
    undefined,
    { skip: !orgId || activeTab !== 'api-keys' }
  );

  const { data: applications } = useGetApplicationsQuery(undefined, {
    skip: !orgId || activeTab !== 'api-keys',
  });

  const [inviteMember, { isLoading: inviting }] = useInviteMemberMutation();
  const [updateMemberRole] = useUpdateMemberRoleMutation();
  const [removeMember] = useRemoveMemberMutation();
  const [cancelInvitation] = useCancelInvitationMutation();
  const [resendInvitation] = useResendInvitationMutation();
  const [updateOrganisation, { isLoading: saving }] = useUpdateOrganisationMutation();
  const [createRole, { isLoading: creatingRole }] = useCreateRoleMutation();
  const [updateRole, { isLoading: updatingRole }] = useUpdateRoleMutation();
  const [deleteRole, { isLoading: deletingRole }] = useDeleteRoleMutation();
  const [createProfile, { isLoading: creatingProfile }] =
    useCreateApiKeyProfileMutation();
  const [regenerateKeys, { isLoading: regenerating }] =
    useRegenerateApiKeyProfileMutation();
  const [deleteProfile] = useDeleteApiKeyProfileMutation();

  const savingRole = creatingRole || updatingRole;

  useEffect(() => {
    if (org) {
      setOrgName(org.name || '');
    }
  }, [org]);

  useEffect(() => {
    if (roles && roles.length > 0 && !inviteRoleId) {
      setInviteRoleId(pickDefaultInviteRoleId(roles));
    }
  }, [roles, inviteRoleId]);

  const handleCopy = async (value: string, message = 'Copied to clipboard.') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopyFeedback(message);
    } catch {
      setCopyFeedback('Could not copy. Select and copy manually.');
    }
  };

  const handleInvite = async () => {
    if (!orgId || !inviteEmail.trim() || !inviteRoleId) return;
    setCopyFeedback(null);
    try {
      const result = await inviteMember({
        orgId,
        email: inviteEmail.trim(),
        roleId: inviteRoleId,
        sendEmail: sendInviteEmail,
      }).unwrap();
      const link = buildInviteLink(result.inviteCode);
      setInviteSuccessLink(link);
      if (sendInviteEmail) {
        setCopyFeedback('Invitation email sent. You can also copy the link below.');
      }
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const handleCloseInviteModal = () => {
    setInviteOpen(false);
    setInviteEmail('');
    setInviteSuccessLink(null);
    setCopyFeedback(null);
    setSendInviteEmail(true);
    if (roles && roles.length > 0) {
      setInviteRoleId(pickDefaultInviteRoleId(roles));
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      const result = await resendInvitation(invitationId).unwrap();
      if (result.inviteCode) {
        await handleCopy(buildInviteLink(result.inviteCode), 'Invite link copied.');
      }
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const handleUpdateRole = async (memberId: string, roleId: string) => {
    try {
      await updateMemberRole({ memberId, roleId }).unwrap();
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    if (!window.confirm('Are you sure you want to remove this member?')) return;
    try {
      await removeMember(memberId).unwrap();
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const handleCancelInvitation = async (invitationId: string) => {
    try {
      await cancelInvitation(invitationId).unwrap();
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const handleSaveSettings = async () => {
    if (!orgId) return;
    try {
      await updateOrganisation({ orgId, body: { name: orgName } }).unwrap();
    } catch {
      // Error handled by RTK Query cache
    }
  };

  const openCreateRoleModal = () => {
    setEditingRole(null);
    setRoleName('');
    setSelectedPermissionSets(['view_only_permission_set']);
    setRoleError(null);
    setRoleModalOpen(true);
  };

  const openEditRoleModal = (role: Role) => {
    setEditingRole(role);
    setRoleName(role.name);
    const sets = getPermissionSetIds(role);
    setSelectedPermissionSets(sets.length > 0 ? sets : ['view_only_permission_set']);
    setRoleError(null);
    setRoleModalOpen(true);
  };

  const handleCloseRoleModal = () => {
    setRoleModalOpen(false);
    setEditingRole(null);
    setRoleName('');
    setSelectedPermissionSets(['view_only_permission_set']);
    setRoleError(null);
  };

  const togglePermissionSet = (id: string) => {
    setSelectedPermissionSets((current) =>
      current.includes(id) ? current.filter((x) => x !== id) : [...current, id]
    );
  };

  const handleSaveRole = async () => {
    if (!orgId) return;
    setRoleError(null);
    if (!roleName.trim()) {
      setRoleError('Role name is required');
      return;
    }
    if (selectedPermissionSets.length === 0) {
      setRoleError('Select at least one permission set');
      return;
    }

    const permissionsConfig = buildPermissionsConfig(selectedPermissionSets);

    try {
      if (editingRole) {
        await updateRole({
          orgId,
          roleId: editingRole.id,
          body: { name: roleName.trim(), permissionsConfig },
        }).unwrap();
      } else {
        await createRole({
          orgId,
          body: { name: roleName.trim(), permissionsConfig },
        }).unwrap();
      }
      handleCloseRoleModal();
    } catch (err) {
      setRoleError(getMutationErrorMessage(err));
    }
  };

  const handleDeleteRole = async (role: Role) => {
    if (!orgId) return;
    if (!window.confirm(`Delete role "${role.name}"?`)) return;
    setRolesActionError(null);
    try {
      await deleteRole({ orgId, roleId: role.id }).unwrap();
    } catch (err) {
      setRolesActionError(getMutationErrorMessage(err));
    }
  };

  const handleCreateProfile = async () => {
    setProfileError(null);
    if (!profileName.trim()) {
      setProfileError('Profile name is required');
      return;
    }
    try {
      const result = await createProfile({
        name: profileName.trim(),
        description: profileDescription.trim() || undefined,
        appIds: selectedAppIds,
      }).unwrap();
      setCreatedProfile(result);
      setShowCreateProfile(false);
      setProfileName('');
      setProfileDescription('');
      setSelectedAppIds([]);
    } catch (err) {
      setProfileError(getMutationErrorMessage(err));
    }
  };

  const handleRegenerate = async (profileId: string) => {
    if (
      !window.confirm(
        'Are you sure you want to regenerate these credentials? All existing integrations using this profile will stop working.'
      )
    ) {
      return;
    }
    try {
      const result = await regenerateKeys(profileId).unwrap();
      setCreatedProfile(result);
    } catch (err) {
      setProfileError(getMutationErrorMessage(err));
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    if (!window.confirm('Delete this API key profile? This cannot be undone.')) return;
    try {
      await deleteProfile(profileId).unwrap();
    } catch (err) {
      setProfileError(getMutationErrorMessage(err));
    }
  };

  const members = membersData?.items || [];
  const totalMembers = membersData?.total ?? org?.memberCount ?? members.length;
  const pendingCount =
    invitations?.length ?? org?.pendingInvitationCount ?? 0;
  const agentCount = org?.agentCount ?? 0;
  const profileCount = org?.apiKeyProfileCount ?? profiles?.length ?? 0;

  const tabs: TabItem[] = useMemo(
    () => [
      { id: 'members', label: 'Members', badge: totalMembers },
      { id: 'api-keys', label: 'API Key Profiles' },
      { id: 'roles', label: 'Roles & Permissions' },
      { id: 'settings', label: 'Settings' },
    ],
    [totalMembers]
  );

  const isLastOwner = (member: Member): boolean => {
    if (!member.roleName || member.roleName.toLowerCase() !== 'owner') return false;
    const ownerCount = members.filter(
      (m) => m.roleName && m.roleName.toLowerCase() === 'owner'
    ).length;
    return ownerCount <= 1;
  };

  const isCurrentUser = (member: Member): boolean =>
    !!user?.id && member.userId === user.id;

  const isOwner = useMemo(() => {
    const me = members.find((m) => isCurrentUser(m));
    return me?.roleName?.toLowerCase() === 'owner';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, user?.id]);

  const memberColumns: Column<Member>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Name',
        width: '22%',
        render: (_val: unknown, row: Member) => (
          <MemberCell>
            <Avatar name={getDisplayName(row)} size="sm" />
            <Text variant="body2" style={{ fontWeight: 500 }}>
              {getDisplayName(row)}
            </Text>
          </MemberCell>
        ),
      },
      {
        key: 'email',
        header: 'Email',
        render: (_val: unknown, row: Member) => <>{row.email || '—'}</>,
      },
      {
        key: 'roleName',
        header: 'Role',
        render: (_val: unknown, row: Member) => {
          const name = row.roleName || '';
          const variant = ROLE_VARIANT_MAP[name.toLowerCase()] ?? 'neutral';
          return (
            <Badge variant={variant} size="sm">
              {name}
            </Badge>
          );
        },
      },
      {
        key: 'joinedDate',
        header: 'Joined',
        render: (_val: unknown, row: Member) => <>{formatDate(row.joinedDate)}</>,
      },
      {
        key: 'lastActive',
        header: 'Last Active',
        render: () => <>—</>,
      },
      {
        key: 'actions',
        header: 'Actions',
        render: (_val: unknown, row: Member) => {
          const lockedOwner = isLastOwner(row);
          const self = isCurrentUser(row);
          if (lockedOwner || (self && row.roleName?.toLowerCase() === 'owner')) {
            return (
              <ActionsCell>
                <span style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>
                  —
                </span>
              </ActionsCell>
            );
          }
          return (
            <ActionsCell>
              <RoleSelect
                value={row.roleId}
                disabled={self}
                title={self ? 'You cannot change your own role' : undefined}
                onChange={(e) => handleUpdateRole(row.id, e.target.value)}
                aria-label={`Change role for ${getDisplayName(row)}`}
              >
                {(roles || []).map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </RoleSelect>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleRemoveMember(row.id)}
                disabled={lockedOwner}
                style={{ color: 'var(--error)' }}
              >
                Remove
              </Button>
            </ActionsCell>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [roles, members, user?.id]
  );

  if (!orgId) {
    return null;
  }

  const orgDisplayName = orgLoading ? 'Loading...' : org?.name || 'Organisation';

  return (
    <Page>
      <OrgBanner>
        <OrgAvatar>{getOrgInitials(org?.name || 'OR')}</OrgAvatar>
        <OrgInfo>
          <OrgTitle>{orgDisplayName}</OrgTitle>
          <OrgSubtitle>
            {orgDescription || 'Manage your organisation, team, and API credentials'}
          </OrgSubtitle>
          <OrgStatsInline>
            <span>{totalMembers} Members</span> · <span>{agentCount} Agents</span> ·{' '}
            <span>{profileCount} API Key Profiles</span>
            {org?.createdAt ? (
              <>
                {' '}
                · <span>Founded {formatDate(org.createdAt)}</span>
              </>
            ) : null}
          </OrgStatsInline>
        </OrgInfo>
      </OrgBanner>

      <KpiStrip>
        <KpiCard>
          <KpiLabel>Team Members</KpiLabel>
          <KpiValue>{totalMembers}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Active Agents</KpiLabel>
          <KpiValue>{agentCount}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>API Key Profiles</KpiLabel>
          <KpiValue>{profileCount}</KpiValue>
        </KpiCard>
        <KpiCard>
          <KpiLabel>Pending Invitations</KpiLabel>
          <KpiValue>{pendingCount}</KpiValue>
        </KpiCard>
      </KpiStrip>

      <TabsBar>
        <Tabs
          items={tabs}
          activeTab={activeTab}
          onChange={(id) => setActiveTab(id as BusinessTab)}
          variant="underline"
        />
      </TabsBar>

      <TabPanel>
        {activeTab === 'members' && (
          <>
            <TabHeader>
              <h3 style={{ margin: 0 }}>Team Members</h3>
              <Button variant="primary" onClick={() => setInviteOpen(true)}>
                + Invite Member
              </Button>
            </TabHeader>

            {membersError && (
              <ErrorBanner>Failed to load members. Please try again.</ErrorBanner>
            )}
            {copyFeedback && activeTab === 'members' && (
              <SuccessBanner style={{ marginBottom: '1rem' }}>{copyFeedback}</SuccessBanner>
            )}

            {membersLoading ? (
              <LoadingContainer>Loading members...</LoadingContainer>
            ) : (
              <Table columns={memberColumns} data={members} rowKey="id" />
            )}

            <PendingSection>
              <h4 style={{ marginBottom: '1rem' }}>Pending Invitations</h4>
              {invitationsLoading ? (
                <LoadingContainer>Loading invitations...</LoadingContainer>
              ) : !invitations || invitations.length === 0 ? (
                <Text variant="body2" color="var(--text-secondary)">
                  No pending invitations.
                </Text>
              ) : (
                invitations.map((inv: Invitation) => (
                  <PendingCard key={inv.id}>
                    <PendingInfo>
                      <PendingEmail>{inv.inviteeEmail}</PendingEmail>
                      <PendingMeta>
                        Invited as {invitationRoleName(inv)} · Sent{' '}
                        {formatRelative(inv.sentAt)}
                      </PendingMeta>
                    </PendingInfo>
                    <PendingActions>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleResendInvitation(inv.id)}
                      >
                        Resend
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCancelInvitation(inv.id)}
                        style={{ color: 'var(--error)' }}
                      >
                        Revoke
                      </Button>
                    </PendingActions>
                  </PendingCard>
                ))
              )}
            </PendingSection>
          </>
        )}

        {activeTab === 'api-keys' && (
          <>
            <TabHeader>
              <TabHeaderText>
                <h3 style={{ margin: 0 }}>API Key Profiles</h3>
                <Text variant="body2" color="var(--text-muted)">
                  Manage API credentials for your consumer applications. Each profile
                  contains an API key and secret key pair used to authenticate API
                  requests.
                </Text>
              </TabHeaderText>
              <Button
                variant="primary"
                onClick={() => {
                  setShowCreateProfile(true);
                  setCreatedProfile(null);
                  setProfileError(null);
                }}
              >
                + Create Profile
              </Button>
            </TabHeader>

            {profileError && <ErrorBanner>{profileError}</ErrorBanner>}

            {createdProfile?.secretKey && (
              <SuccessCard>
                <h4 style={{ color: 'var(--success)', marginTop: 0 }}>
                  Profile Created Successfully
                </h4>
                <SuccessField>
                  <SuccessFieldLabel>Profile ID</SuccessFieldLabel>
                  <KeyDisplay>
                    <KeyValue>{createdProfile.profileId}</KeyValue>
                    <CopyBtn
                      type="button"
                      onClick={() => handleCopy(createdProfile.profileId)}
                    >
                      Copy
                    </CopyBtn>
                  </KeyDisplay>
                </SuccessField>
                <SuccessField>
                  <SuccessFieldLabel>API Key</SuccessFieldLabel>
                  <KeyDisplay>
                    <KeyValue>{createdProfile.apiKey}</KeyValue>
                    <CopyBtn
                      type="button"
                      onClick={() => handleCopy(createdProfile.apiKey)}
                    >
                      Copy
                    </CopyBtn>
                  </KeyDisplay>
                </SuccessField>
                <SuccessField>
                  <SuccessFieldLabel>Secret Key</SuccessFieldLabel>
                  <KeyDisplay>
                    <KeyValue>{createdProfile.secretKey}</KeyValue>
                    <CopyBtn
                      type="button"
                      onClick={() => handleCopy(createdProfile.secretKey!)}
                    >
                      Copy
                    </CopyBtn>
                  </KeyDisplay>
                </SuccessField>
                <InfoBanner $variant="warning" style={{ marginTop: '0.75rem' }}>
                  <span>⚠</span>
                  <span>
                    The secret key will not be shown again. Copy it now. The API key
                    can be retrieved later from the profile.
                  </span>
                </InfoBanner>
                <Button variant="primary" onClick={() => setCreatedProfile(null)}>
                  Done
                </Button>
              </SuccessCard>
            )}

            {profilesLoading ? (
              <LoadingContainer>Loading profiles...</LoadingContainer>
            ) : (
              (profiles || []).map((profile) => (
                <ProfileCard key={profile.profileId}>
                  <ProfileCardHeader>
                    <div>
                      <ProfileName>{profile.name}</ProfileName>
                      <ProfileId>{profile.profileId}</ProfileId>
                    </div>
                    <Badge
                      variant={profile.status === 'active' ? 'success' : 'neutral'}
                      size="sm"
                    >
                      <StatusDot /> {profile.status === 'active' ? 'Active' : profile.status}
                    </Badge>
                  </ProfileCardHeader>

                  {profile.applications && profile.applications.length > 0 && (
                    <ProfileApps>
                      {profile.applications.map((app) => (
                        <AppChip key={app.appId}>{app.name}</AppChip>
                      ))}
                    </ProfileApps>
                  )}

                  <ProfileMeta>
                    <ProfileMetaItem>
                      <ProfileMetaLabel>Created</ProfileMetaLabel>
                      <span>{formatDate(profile.createdAt)}</span>
                    </ProfileMetaItem>
                    <ProfileMetaItem>
                      <ProfileMetaLabel>Last Used</ProfileMetaLabel>
                      <span>{formatRelative(profile.lastUsedAt)}</span>
                    </ProfileMetaItem>
                  </ProfileMeta>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '0.75rem',
                    }}
                  >
                    <div>
                      <ProfileMetaLabel>API Key</ProfileMetaLabel>
                      <KeyDisplay>
                        <KeyValue>{profile.apiKey}</KeyValue>
                        <CopyBtn
                          type="button"
                          onClick={() => handleCopy(profile.apiKey)}
                        >
                          Copy
                        </CopyBtn>
                      </KeyDisplay>
                    </div>
                    <div>
                      <ProfileMetaLabel>Secret Key</ProfileMetaLabel>
                      <KeyDisplay>
                        <KeyValue>
                          {profile.secretKeyMasked || 'lf_sk_****...****'}
                        </KeyValue>
                        <CopyBtn type="button" disabled title="Secret cannot be copied after creation">
                          Copy
                        </CopyBtn>
                      </KeyDisplay>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handleRegenerate(profile.profileId)}
                        disabled={regenerating}
                      >
                        Regenerate
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDeleteProfile(profile.profileId)}
                        style={{ color: 'var(--error)' }}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </ProfileCard>
              ))
            )}

            {showCreateProfile && (
              <CreateProfileCard>
                <h4 style={{ marginTop: 0 }}>New API Key Profile</h4>
                <InfoBanner $variant="info">
                  <span>ℹ</span>
                  <span>
                    Both the API key and secret key will be generated. The secret key
                    will only be shown once after creation — make sure to copy it.
                  </span>
                </InfoBanner>
                <FormFields>
                  <Input
                    label="Profile Name"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="e.g. Development API Key"
                    fullWidth
                  />
                  <div>
                    <SelectLabel>Description</SelectLabel>
                    <TextArea
                      value={profileDescription}
                      onChange={(e) => setProfileDescription(e.target.value)}
                      placeholder="What is this key used for?"
                      rows={2}
                    />
                  </div>
                  <div>
                    <SelectLabel>Applications</SelectLabel>
                    <CheckboxGroup>
                      {(applications || []).map((app) => (
                        <CheckboxLabel key={app.appId}>
                          <input
                            type="checkbox"
                            checked={selectedAppIds.includes(app.appId)}
                            onChange={() =>
                              setSelectedAppIds((curr) =>
                                curr.includes(app.appId)
                                  ? curr.filter((id) => id !== app.appId)
                                  : [...curr, app.appId]
                              )
                            }
                          />
                          {app.name}
                        </CheckboxLabel>
                      ))}
                      {(!applications || applications.length === 0) && (
                        <Text variant="body2" color="var(--text-muted)">
                          No applications yet. Create an application first to associate
                          it.
                        </Text>
                      )}
                    </CheckboxGroup>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <Button
                      variant="primary"
                      onClick={handleCreateProfile}
                      disabled={creatingProfile || !profileName.trim()}
                    >
                      {creatingProfile ? 'Creating...' : 'Create Profile'}
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setShowCreateProfile(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                </FormFields>
              </CreateProfileCard>
            )}
          </>
        )}

        {activeTab === 'roles' && (
          <>
            <TabHeader>
              <h3 style={{ margin: 0 }}>Roles &amp; Permissions</h3>
              <Button variant="primary" onClick={openCreateRoleModal}>
                New custom role
              </Button>
            </TabHeader>

            {rolesActionError && <ErrorBanner>{rolesActionError}</ErrorBanner>}

            {rolesLoading ? (
              <LoadingContainer>Loading roles...</LoadingContainer>
            ) : (
              <RolesGrid>
                {(roles || []).map((role: Role) => {
                  const key = role.name.toLowerCase();
                  const variant = ROLE_VARIANT_MAP[key] ?? 'neutral';
                  const count = role.memberCount ?? 0;
                  const description =
                    SYSTEM_ROLE_DESCRIPTIONS[key] ||
                    (role.isSystemRole
                      ? 'System role'
                      : 'Custom organisation role');
                  const chips =
                    SYSTEM_ROLE_PERMISSION_CHIPS[key] ||
                    getPermissionSetIds(role)
                      .map((id) => PERMISSION_SET_LABELS[id] || id)
                      .filter(Boolean);
                  const readonlyChips = key === 'viewer';

                  return (
                    <RoleCard key={role.id}>
                      <RoleCardHeader>
                        <Badge variant={variant} size="sm">
                          {role.name}
                        </Badge>
                        <RoleMemberCount>
                          {count} member{count === 1 ? '' : 's'}
                        </RoleMemberCount>
                      </RoleCardHeader>
                      <RoleDescription>{description}</RoleDescription>
                      <PermissionsList>
                        {chips.map((chip) => (
                          <PermissionChip key={chip} $readonly={readonlyChips}>
                            {chip}
                          </PermissionChip>
                        ))}
                      </PermissionsList>
                      <div style={{ marginTop: '0.75rem' }}>
                        <Badge
                          variant={role.isSystemRole ? 'neutral' : 'info'}
                          size="sm"
                        >
                          {role.isSystemRole ? 'System' : 'Custom'}
                        </Badge>
                      </div>
                      {!role.isSystemRole && (
                        <RoleActions>
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => openEditRoleModal(role)}
                          >
                            Edit
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteRole(role)}
                            disabled={deletingRole}
                          >
                            Delete
                          </Button>
                        </RoleActions>
                      )}
                    </RoleCard>
                  );
                })}
              </RolesGrid>
            )}
          </>
        )}

        {activeTab === 'settings' && (
          <>
            <TabHeader>
              <h3 style={{ margin: 0 }}>Organisation Settings</h3>
            </TabHeader>

            <Card padding={24} elevation="sm" style={{ marginBottom: '1.5rem' }}>
              <SettingsForm>
                <Input
                  label="Organisation Name"
                  value={orgName}
                  onChange={(e) => setOrgName(e.target.value)}
                  fullWidth
                />
                <div>
                  <SelectLabel>Description</SelectLabel>
                  <TextArea
                    value={orgDescription}
                    onChange={(e) => setOrgDescription(e.target.value)}
                    rows={3}
                    placeholder="Short description of your organisation"
                  />
                </div>
                <div>
                  <SelectLabel>Logo</SelectLabel>
                  <UploadPlaceholder title="Logo upload coming soon">
                    Upload
                    <br />
                    logo
                  </UploadPlaceholder>
                </div>
                <Button
                  variant="primary"
                  onClick={handleSaveSettings}
                  disabled={saving}
                  style={{ alignSelf: 'flex-start' }}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </SettingsForm>
            </Card>

            <DangerZone>
              <h4>Dissolve Organisation</h4>
              <p>
                Dissolving this organisation will permanently remove all members,
                agents, data sources, and API key profiles associated with it. This
                action cannot be undone.
              </p>
              <Button
                variant="danger"
                disabled
                title={
                  isOwner
                    ? 'Dissolve is not available yet'
                    : 'Only the owner can dissolve'
                }
              >
                Dissolve Organisation
              </Button>
            </DangerZone>
          </>
        )}
      </TabPanel>

      <Modal isOpen={inviteOpen} onClose={handleCloseInviteModal} title="Invite Member">
        <ModalForm>
          {inviteSuccessLink ? (
            <>
              {copyFeedback && <SuccessBanner>{copyFeedback}</SuccessBanner>}
              <Text variant="body2">
                Share this link with your colleague if they did not receive the email:
              </Text>
              <InviteLinkRow>
                <InviteLinkInput readOnly value={inviteSuccessLink} />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleCopy(inviteSuccessLink, 'Invite link copied.')}
                >
                  Copy link
                </Button>
              </InviteLinkRow>
              <ModalActions>
                <Button variant="primary" onClick={handleCloseInviteModal}>
                  Done
                </Button>
              </ModalActions>
            </>
          ) : (
            <>
              <Input
                label="Email Address"
                type="email"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                placeholder="colleague@company.com"
                fullWidth
              />
              <SelectWrapper>
                <SelectLabel htmlFor="invite-role">Role</SelectLabel>
                <Select
                  id="invite-role"
                  value={inviteRoleId}
                  onChange={(e) => setInviteRoleId(e.target.value)}
                >
                  {(roles || []).map((r: Role) => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </Select>
              </SelectWrapper>
              <CheckboxLabel>
                <input
                  type="checkbox"
                  checked={sendInviteEmail}
                  onChange={(e) => setSendInviteEmail(e.target.checked)}
                />
                Send invitation email
              </CheckboxLabel>
              <ModalActions>
                <Button variant="ghost" onClick={handleCloseInviteModal}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleInvite}
                  disabled={!inviteEmail.trim() || !inviteRoleId || inviting}
                >
                  {inviting
                    ? 'Sending...'
                    : sendInviteEmail
                      ? 'Send Invitation'
                      : 'Create invite link'}
                </Button>
              </ModalActions>
            </>
          )}
        </ModalForm>
      </Modal>

      <Modal
        isOpen={roleModalOpen}
        onClose={handleCloseRoleModal}
        title={editingRole ? 'Edit custom role' : 'New custom role'}
      >
        <ModalForm>
          <Input
            label="Name"
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            placeholder="e.g. Editor"
            fullWidth
          />
          <div>
            <SelectLabel>Permission sets</SelectLabel>
            <CheckboxGroup>
              {PERMISSION_SETS.map((p) => (
                <CheckboxLabel key={p.id}>
                  <input
                    type="checkbox"
                    checked={selectedPermissionSets.includes(p.id)}
                    onChange={() => togglePermissionSet(p.id)}
                  />
                  {p.label}
                </CheckboxLabel>
              ))}
            </CheckboxGroup>
          </div>
          {roleError && <ErrorBanner>{roleError}</ErrorBanner>}
          <ModalActions>
            <Button variant="ghost" onClick={handleCloseRoleModal} disabled={savingRole}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveRole}
              disabled={savingRole || !roleName.trim()}
            >
              {savingRole
                ? 'Saving...'
                : editingRole
                  ? 'Save changes'
                  : 'Create role'}
            </Button>
          </ModalActions>
        </ModalForm>
      </Modal>
    </Page>
  );
};

export default BusinessCenter;
